'use client';
import React, { useState, useEffect } from 'react';

interface HeaderProps {
  onReservarClick: () => void;
  lang: 'es' | 'en';
  onLangChange: (lang: 'es' | 'en') => void;
}

export default function Header({ onReservarClick, lang, onLangChange }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = lang === 'es'
    ? [
        { label: 'Inicio', id: 'inicio' },
        { label: 'El Cenote', id: 'cenote' },
        { label: 'Experiencias', id: 'experiencias' },
        { label: 'Contacto', id: 'contacto' },
      ]
    : [
        { label: 'Home', id: 'inicio' },
        { label: 'The Cenote', id: 'cenote' },
        { label: 'Experiences', id: 'experiencias' },
        { label: 'Contact', id: 'contacto' },
      ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-lux-black/96 backdrop-blur-md shadow-2xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollTo('inicio')}>
            <div className="w-7 h-7 border border-lux-gold flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-lux-gold" stroke="currentColor" strokeWidth="1">
                <path d="M12 3C9 3 5 6.5 5 10.5c0 5 7 11 7 11s7-6 7-11C19 6.5 15 3 12 3z" />
                <circle cx="12" cy="10" r="2.5" fill="currentColor" />
              </svg>
            </div>
            <span className="text-white font-serif text-base font-light tracking-[0.2em] uppercase">Cenote Privado</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-white/60 hover:text-white text-xs font-sans font-medium tracking-[0.15em] uppercase transition-colors duration-300"
              >
                {item.label}
              </button>
            ))}

            {/* Language Toggle */}
            <div className="flex items-center gap-1 border border-white/20 rounded-none px-1 py-1">
              <button
                onClick={() => onLangChange('es')}
                className={`px-2 py-0.5 text-xs font-medium tracking-widest uppercase transition-all duration-200 ${
                  lang === 'es' ? 'bg-white text-lux-black' : 'text-white/50 hover:text-white'
                }`}
              >
                ES
              </button>
              <button
                onClick={() => onLangChange('en')}
                className={`px-2 py-0.5 text-xs font-medium tracking-widest uppercase transition-all duration-200 ${
                  lang === 'en' ? 'bg-white text-lux-black' : 'text-white/50 hover:text-white'
                }`}
              >
                EN
              </button>
            </div>

            <button
              onClick={onReservarClick}
              className="border border-lux-gold text-lux-gold hover:bg-lux-gold hover:text-lux-black px-6 py-2 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-300"
            >
              {lang === 'es' ? 'Reservar' : 'Book Now'}
            </button>
          </nav>

          {/* Mobile controls */}
          <div className="md:hidden flex items-center gap-3">
            <div className="flex items-center gap-1 border border-white/20 px-1 py-1">
              <button
                onClick={() => onLangChange('es')}
                className={`px-2 py-0.5 text-xs font-medium tracking-widest uppercase transition-all duration-200 ${
                  lang === 'es' ? 'bg-white text-lux-black' : 'text-white/50'
                }`}
              >
                ES
              </button>
              <button
                onClick={() => onLangChange('en')}
                className={`px-2 py-0.5 text-xs font-medium tracking-widest uppercase transition-all duration-200 ${
                  lang === 'en' ? 'bg-white text-lux-black' : 'text-white/50'
                }`}
              >
                EN
              </button>
            </div>
            <button
              className="text-white p-1"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-lux-black border-t border-white/10 py-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="block w-full text-left text-white/60 hover:text-white px-4 py-3 text-xs font-medium tracking-[0.15em] uppercase"
              >
                {item.label}
              </button>
            ))}
            <div className="px-4 pt-4">
              <button
                onClick={() => { setMenuOpen(false); onReservarClick(); }}
                className="w-full border border-lux-gold text-lux-gold hover:bg-lux-gold hover:text-lux-black px-5 py-3 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-300"
              >
                {lang === 'es' ? 'Reservar Ahora' : 'Book Now'}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
